import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Dropdown, Button, Divider } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ChevronDownIcon } from 'lucide-react';

export type AIStateKey =
  | 'resolved'
  | 'dismissed'
  | 'processing'
  | 'needs_attention'
  | 'resolved_via_ai';

export interface RawAIStateItem {
  id: AIStateKey;
  title: string;
  count: number;
}

export interface RawAIStateSection {
  type: 'section';
  label: string;
  items: RawAIStateItem[];
}

export interface GetAIStateCountsRes {
  data: {
    is_success: boolean;
    message?: string;
    messageTypeId?: string;
    page_count?: number;
    status?: number;
    data: RawAIStateSection[];
  };
}

interface AIStateItem {
  id: AIStateKey;
  title: string;
  count: string;
}

export interface AIStateSection {
  type: 'section';
  label: string;
  items: AIStateItem[];
}

type AIStateFilterMode = 'dropdown' | 'pill' | 'both';

interface AIStateFilterProps {
  selectedStates: AIStateKey[];
  onChange: (selected: AIStateKey[]) => void;
  sections: RawAIStateSection[];
  mode?: AIStateFilterMode;
}

const AIStateFilter: React.FC<AIStateFilterProps> = ({
  selectedStates,
  onChange,
  sections,
  mode = 'both',
}) => {
  const [mainDropdownOpen, setMainDropdownOpen] = useState(false);
  const [pillDropdownOpen, setPillDropdownOpen] = useState(false);

  const [tempSelected, setTempSelected] =
    useState<AIStateKey[]>(selectedStates);

  useEffect(() => {
    if (mainDropdownOpen || pillDropdownOpen) {
      setTempSelected(selectedStates);
    }
  }, [mainDropdownOpen, pillDropdownOpen, selectedStates]);

  const aiStateOptions: AIStateSection[] = useMemo(() => {
    return (sections ?? []).map((s) => ({
      type: 'section',
      label: String(s?.label ?? ''),
      items: (s?.items ?? []).map((it) => ({
        id: it.id,
        title: String(it?.title ?? ''),
        count: String(it?.count ?? '0'),
      })),
    }));
  }, [sections]);

  const allItems = useMemo(
    () => aiStateOptions.flatMap((s) => s.items),
    [aiStateOptions]
  );

  const handleSelect = (id: AIStateKey, checked: boolean) => {
    setTempSelected((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const handleApply = () => {
    onChange(tempSelected);
    setMainDropdownOpen(false);
    setPillDropdownOpen(false);
  };

  const handleReset = () => {
    onChange([]);
    setTempSelected([]);
    setMainDropdownOpen(false);
    setPillDropdownOpen(false);
  };

  const menuContent = (
    <div
      style={{
        backgroundColor: 'var(--surface-elevated)',
        width: 240,
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        padding: '12px 16px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {aiStateOptions.map((section, index) => (
        <div key={section.label} style={{ marginBottom: 8 }}>
          <div className='zl-para-400-12 mb-1' style={{ fontStyle: 'italic' }}>
            {section.label}
          </div>

          {section.items.map((item) => (
            <div key={item.id} style={{ padding: '4px 0' }}>
              <Checkbox
                checked={tempSelected.includes(item.id)}
                onChange={(e) => handleSelect(item.id, e.target.checked)}
                className='custom-menu-shipment-checkbox-only checkbox-sm'
                style={{ fontSize: 12 }}
              >
                {item.title} ({item.count})
              </Checkbox>
            </div>
          ))}

          {index === 0 && <Divider style={{ margin: '8px 0px' }} />}
        </div>
      ))}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 12,
          gap: 8,
        }}
      >
        <Button
          type='text'
          onClick={handleReset}
          style={{ fontSize: 12, height: 28, width: 52, fontWeight: 600 }}
        >
          Reset
        </Button>

        <Button
          type='primary'
          onClick={handleApply}
          style={{
            fontWeight: 600,
            height: 28,
            width: 58,
            fontSize: 12,
            borderRadius: 8,
            backgroundColor: 'var(--text-primary)',
            borderColor: 'var(--text-primary)',
            color: 'var(--surface-base)',
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  const firstSelected = selectedStates[0];
  const firstTitle =
    allItems.find((o) => o.id === firstSelected)?.title ?? firstSelected;

  const showDropdown = mode === 'both' || mode === 'dropdown';
  const showPill = mode === 'both' || mode === 'pill';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {showDropdown && (
        <div className='ai-state-filter'>
          <Dropdown
            dropdownRender={() => menuContent}
            trigger={['click']}
            open={mainDropdownOpen}
            onOpenChange={(flag) => {
              setMainDropdownOpen(flag);
              if (flag) setPillDropdownOpen(false);
            }}
          >
            <button
              className='zl-btn zl-btn--10-600 rounded-full flex-center'
              type='button'
            >
              AI state <ChevronDownIcon size={10} />
            </button>
          </Dropdown>
        </div>
      )}

      {showPill && selectedStates.length > 0 && (
        <Dropdown
          dropdownRender={() => menuContent}
          trigger={['click']}
          open={pillDropdownOpen}
          onOpenChange={(flag) => {
            setPillDropdownOpen(flag);
            if (flag) setMainDropdownOpen(false);
          }}
        >
          <button
            className='zl-btn zl-btn-clr rounded-full flex-center'
            style={{ whiteSpace: 'nowrap', gap: 6 }}
            type='button'
          >
            AI state: {firstTitle}
            {selectedStates.length > 1 ? ` +${selectedStates.length - 1}` : ''}
            <span
              className='ai-state-close'
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            >
              <CloseOutlined />
            </span>
          </button>
        </Dropdown>
      )}
    </div>
  );
};

export default AIStateFilter;
